import React, { useEffect, useMemo, useState } from "react";
import { Box, CreateFixedTermDeposit } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import moment from "moment-timezone";
import * as Yup from "yup";
import { arrayOf, bool, func, number, shape, string } from "prop-types";
import { push, replace } from "react-router-redux";

import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

import Notification from "../../_components/Notification";
import Head from "../../_components/Head";

import { selectors as sessionSelectors } from "../../../reducers/session";
import { actions as accountsActions, selectors as accountsSelectors } from "../../../reducers/accounts";
import { selectors as depositsSelectors, actions as depositsActions } from "../../../reducers/deposits";
import { selectors as statusSelectors } from "../../../reducers/status";

import * as i18n from "../../../util/i18n";
import * as configUtil from "../../../util/config";
import { getAccountName } from "../../../util/accounts";
import {
    getCurrencyFromType,
    getIsoCurrency,
    normalizeOptionValue,
    normalizeNumber,
    calculateDueDate,
    syncTermToDate,
    syncDateToTerm,
    validateAndResetTerm,
} from "../../../util/deposits";

const ARS_ISO_CODE = "ARS";
const INVALID_ACCOUNT = "CA";
const MAX_TERM_DAYS = 999;

function DepositsConstituteV2({
    dispatch,
    loggedUser,
    accounts,
    fetchingAccounts,
    minAmountPesos,
    minAmountDolares,
    depositTypes,
    environmentDate,
    defaultDaysQuantity,
    loadingPreview,
    loadingSimulation,
    loadingConstitution,
    simulationResult,
    isRetailEnvironment,
    isDesktop,
    location,
    lastPathname,
    embeddedInV3Shell = false,
}) {
    const [formInstance, setFormInstance] = useState(0);
    const [simulationInput, setSimulationInput] = useState(null);
    const [automaticRenewal, setAutomaticRenewal] = useState(false);
    const [formikPropsRef, setFormikPropsRef] = useState(null);
    const [selectedCurrencyCode, setSelectedCurrencyCode] = useState(() => {
        const defaultType = depositTypes?.length ? depositTypes[0]?.value : "";
        return getCurrencyFromType(defaultType);
    });

    useEffect(() => {
        dispatch(depositsActions.simulateDepositPreview());
        if (!accounts.length) {
            dispatch(accountsActions.listAccounts(loggedUser.userId));
        }
    }, [accounts.length, dispatch, loggedUser.userId]);

    useEffect(() => {
        applyEmbeddedV3ShellLayoutState(dispatch, {
            embeddedInV3Shell,
            isDesktop,
            scopes: ["depositsConstitute"],
            titleKey: "deposits.constitute.title",
            goBackPath: "/deposits",
        });
    }, [dispatch, embeddedInV3Shell, isDesktop]);

    useEffect(
        () => () => {
            if (embeddedInV3Shell) {
                clearEmbeddedV3ShellLayoutState(dispatch);
            }
        },
        [embeddedInV3Shell, dispatch],
    );

    useEffect(() => {
        if (depositTypes?.length && !selectedCurrencyCode) {
            setSelectedCurrencyCode(getCurrencyFromType(depositTypes[0]?.value));
        }
    }, [depositTypes, selectedCurrencyCode]);

    useEffect(() => {
        const timeExtended = configUtil.getBoolean("frontend.PlazoFijo.timeExtended", false);
        const currentPath = location?.pathname;
        const expectedPath = timeExtended ? "/v2/depositsConstitute" : "/depositsConstitute";

        if (currentPath && currentPath !== expectedPath) {
            dispatch(replace(expectedPath));
        }
    }, [dispatch, location?.pathname]);

    const typeOptions = useMemo(
        () =>
            (depositTypes || []).map((type) => ({
                value: type.value,
                label: type.label,
            })),
        [depositTypes],
    );

    const selectedCurrency = useMemo(() => {
        const currency = getIsoCurrency(selectedCurrencyCode);
        return currency;
    }, [selectedCurrencyCode]);

    const minAmount = useMemo(() => {
        const amount = selectedCurrencyCode === "2" ? minAmountDolares ?? 0 : minAmountPesos ?? 0;
        return amount;
    }, [selectedCurrencyCode, minAmountPesos, minAmountDolares]);

    const actionToDueOptions = [
        {
            value: "capitalRenovationPlusInterests",
            label: i18n.get("deposits.constitute.actionToDue.options.capitalRenovationPlusInterests"),
        },
    ];

    const handleTypeChange = (typeValue) => {
        const currencyCode = getCurrencyFromType(typeValue);
        setSelectedCurrencyCode(currencyCode);

        const typeOption = typeOptions.find((opt) => opt.value === typeValue);

        if (formikPropsRef?.setFieldValue && typeOption) {
            formikPropsRef.setFieldValue("type", typeOption);
            const newMinAmount = currencyCode === "2" ? minAmountDolares ?? 0 : minAmountPesos ?? 0;
            formikPropsRef.setFieldValue("amount", newMinAmount);
        }
    };

    const filteredAccountsByCurrency = useMemo(
        () =>
            (accounts || []).filter((account) => {
                const matchesCurrency = String(account?.currency) === selectedCurrencyCode;
                if (!matchesCurrency) {
                    return false;
                }
                return isRetailEnvironment ? true : String(account?.productType) !== INVALID_ACCOUNT;
            }),
        [accounts, isRetailEnvironment, selectedCurrencyCode],
    );

    const accountsOptions = useMemo(
        () =>
            filteredAccountsByCurrency.map((account) => ({
                id: account.idProduct,
                accountName: account.productAlias || account.shortLabel || getAccountName(account),
                description: i18n.get(`productType.${account.productType}`),
                currency: selectedCurrency,
                amount: account.balance || 0,
            })),
        [filteredAccountsByCurrency, selectedCurrency],
    );

    const defaultAccountId = useMemo(() => {
        if (filteredAccountsByCurrency.length === 1) {
            return filteredAccountsByCurrency[0].idProduct;
        }
        if (filteredAccountsByCurrency.length > 1) {
            const favoriteAccount = filteredAccountsByCurrency.find((account) => account.favorite);
            return favoriteAccount?.idProduct || "";
        }
        return "";
    }, [filteredAccountsByCurrency]);

    const minDueDate = useMemo(() => {
        if (!environmentDate) {
            return new Date();
        }
        return calculateDueDate(environmentDate, defaultDaysQuantity);
    }, [defaultDaysQuantity, environmentDate]);

    const maxDueDate = useMemo(
        () =>
            moment(minDueDate)
                .add(MAX_TERM_DAYS, "days")
                .toDate(),
        [minDueDate],
    );

    const initialValuesSimulate = useMemo(() => {
        const defaultTypeOption = depositTypes?.length
            ? depositTypes.find((type) => getCurrencyFromType(type.value) === selectedCurrencyCode) || depositTypes[0]
            : null;
        const defaultAmount = minAmount ?? "";
        const defaultTerm = defaultDaysQuantity ?? "";
        const defaultDueDate =
            minDueDate && minDueDate instanceof Date && !Number.isNaN(minDueDate.getTime()) ? minDueDate : null;
        return {
            type: defaultTypeOption,
            amount: defaultAmount,
            term: defaultTerm,
            dueDate: defaultDueDate,
        };
    }, [depositTypes, defaultDaysQuantity, minAmount, minDueDate, selectedCurrencyCode]);

    const initialValuesCreate = useMemo(
        () => ({
            account: defaultAccountId,
            automaticRenewal: false,
            actionToDue: "capitalRenovationPlusInterests",
        }),
        [defaultAccountId],
    );

    const validationSchemaSimulate = useMemo(
        () =>
            Yup.object().shape({
                type: Yup.mixed().required(i18n.get("deposits.constitute.field.error.required")),
                amount: Yup.number()
                    .transform((_, originalValue) => normalizeNumber(originalValue) ?? undefined)
                    .required(i18n.get("deposits.constitute.field.error.required"))
                    .min(minAmount, i18n.get("deposits.constitute.consolidatedAmount.field.error.minAmount")),
                term: Yup.number()
                    .required(i18n.get("deposits.constitute.field.error.required"))
                    .min(defaultDaysQuantity, i18n.get("deposits.constitute.termInDays.field.error.invalidDate")),
                dueDate: Yup.date().required(i18n.get("deposits.constitute.dueDate.error.required")),
            }),
        [minAmount, defaultDaysQuantity],
    );

    const validationSchemaCreate = Yup.object().shape({
        account: Yup.string().required(i18n.get("deposits.constitute.debitAccount.field.error.required")),
        automaticRenewal: Yup.boolean(),
    });

    const getFormLabel = (id) => {
        const labels = {
            type: i18n.get(`deposits.constitute.type.label.minus`),
            amount: i18n.get(`deposits.constitute.consolidatedAmount.label.minus`),
            minAmountLabel: i18n.get("deposits.constitute.consolidatedAmountMin.label.minus"),
            termOnDays: i18n.get(`deposits.constitute.termInDays.label.minus`),
            dueDate: i18n.get(`deposits.constitute.dueDate.label.minus`),
            dueDateSimulate: i18n.get("deposits.constitute.dueDate.label"),
            account: i18n.get(`deposits.constitute.debitAccount.label.minus`),
            automaticRenewal: i18n.get("deposits.constitute.autoRenew.label.minus"),
            actionToDue: i18n.get("deposits.constitute.actionToDue.label.minus"),
            cancel: i18n.get("global.cancel"),
            simulateSubmit: i18n.get(`deposits.constitute.simulation.button.minus`),
            simulateAgain: i18n.get("deposits.constitute.confirmation.goBack.minus"),
            createSubmit: i18n.get("deposits.constitute.confirmation.button.minus"),
            selectPlaceholder: i18n.get("transfers.productSelector.placeholder"),
            selectAccountPlaceholder: i18n.get("transfers.productSelector.placeholder"),
            earnedInterest: i18n.get("deposits.constitute.earnedInterests.label.minus"),
            tna: i18n.get("deposits.constitute.rate.short.label"),
            termInDays: i18n.get("deposits.constitute.termInDays.label.minus"),
            amountToCollect: i18n.get("deposits.constitute.totalAmount.label.minus"),
        };
        return labels[id] || id;
    };

    const formatCurrency = (amount) => {
        const numberAmount = typeof amount === "number" ? amount : Number(amount);
        const safeAmount = Number.isNaN(numberAmount) ? 0 : numberAmount;
        return new Intl.NumberFormat("es-AR", { style: "currency", currency: ARS_ISO_CODE }).format(safeAmount);
    };

    const simulateData = useMemo(() => {
        if (!simulationResult) {
            return undefined;
        }
        return {
            earnedInterest: formatCurrency(simulationResult.earnedInterests?.quantity),
            tna: `${simulationResult.rate}%`,
            termInDays: simulationInput?.termInDays ?? defaultDaysQuantity,
            dueDate: moment(simulationResult.dueDate).format("DD/MM/YYYY"),
            amountToCollect: simulationResult.totalAmount?.quantity ?? 0,
        };
    }, [defaultDaysQuantity, simulationInput, simulationResult]);

    const handleCancel = () => dispatch(push("/deposits"));

    const handleBack = () => {
        if (embeddedInV3Shell) {
            dispatch(push("/deposits"));
            return;
        }
        if (lastPathname) {
            dispatch(push(lastPathname));
        } else {
            dispatch(push("/deposits"));
        }
    };

    const handleSimulateAgain = () => {
        dispatch(depositsActions.goBackToSimulation());
        setSimulationInput(null);
        setAutomaticRenewal(false);
        const defaultType = depositTypes?.length ? depositTypes[0]?.value : "";
        const defaultCurrencyCode = getCurrencyFromType(defaultType);
        setSelectedCurrencyCode(defaultCurrencyCode);
        setFormInstance((value) => value + 1);
    };

    const handleDueDateChange = (date) => {
        syncDateToTerm(formikPropsRef, environmentDate, date, defaultDaysQuantity);
    };

    const handleTermChange = (term) => {
        if (formikPropsRef) {
            formikPropsRef.setFieldTouched("term", true, false);
            const termValue = term === 0 || term === "" || term === null || term === undefined ? "" : term;
            if (termValue !== "") {
                syncTermToDate(formikPropsRef, environmentDate, term);
            }
            setTimeout(() => {
                formikPropsRef.validateField("term");
            }, 0);
        }
    };

    const handleTermBlur = (value) => {
        validateAndResetTerm(formikPropsRef, environmentDate, value, defaultDaysQuantity);
    };

    const handleAutomaticRenewalChange = () => {
        setAutomaticRenewal(!automaticRenewal);
    };

    const handleSubmitSimulate = (values, formikBag) => {
        const depositType = normalizeOptionValue(values.type);
        const termInDays = normalizeNumber(values.term);
        const amount = normalizeNumber(values.amount);
        const currencyCode = getCurrencyFromType(depositType);
        const consolidatedAmount = { currency: Number(currencyCode), amount };
        const timeZone = "America/Argentina/Buenos_Aires";
        const localDueDate = values.dueDate ? moment(values.dueDate).format("YYYY-MM-DD") : "";
        const dueDate = localDueDate ? moment.tz(localDueDate, "YYYY-MM-DD", timeZone).toISOString() : "";
        setSimulationInput({
            depositType,
            consolidatedAmount,
            termInDays,
            dueDate,
        });

        dispatch(
            depositsActions.simulateDeposit({
                depositType,
                consolidatedAmount,
                termInDays,
                dueDate,
                formikBag,
            }),
        );
    };

    const handleSubmitCreate = (values, formikBag) => {
        if (!simulationResult || !simulationInput) {
            return;
        }
        const accountId = normalizeOptionValue(values.account);
        const selectedAccount = accounts.find((account) => account.idProduct === accountId);
        if (!selectedAccount) {
            return;
        }

        const {
            rate,
            capital,
            totalAmount,
            earnedInterests,
            requestCode,
            operationNumber,
            profitTaxCode,
            profitTax,
            witnessRate,
            codeRate,
            accountNumber,
        } = simulationResult;

        const actionToDueValue = automaticRenewal ? normalizeOptionValue(values.actionToDue) : undefined;

        dispatch(
            depositsActions.constituteDeposit({
                debitAccount: selectedAccount,
                depositType: simulationInput.depositType,
                consolidatedAmount: simulationInput.consolidatedAmount,
                termInDays: simulationInput.termInDays,
                dueDate: simulationInput.dueDate,
                automaticRenewal,
                actionToDue: actionToDueValue,
                rate,
                capital,
                totalAmount,
                earnedInterests,
                requestCode,
                operationNumber,
                profitTaxCode,
                profitTax,
                witnessRate,
                codeRate,
                accountNumber,
                formikBag,
            }),
        );
    };

    const loading = loadingPreview || fetchingAccounts;
    const loadingSubmit = loadingSimulation || loadingConstitution;
    const showCreateForm = Boolean(simulationResult);

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="depositsConstitute" />}
            {!(embeddedInV3Shell && !isDesktop) && (
                <Box className="admin-detail-head" px={isDesktop ? 40 : 24}>
                    {isDesktop && <Head onBack={handleBack} />}
                    <Head
                        title="deposits.constitute.title"
                        onBack={!isDesktop && handleBack}
                        titleClassName={
                            !isDesktop ? "header-mobile-title-background-blue overflow-visible" : "my-3 align-center"
                        }
                        headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                        {...(isDesktop && {
                            messageTooltip: configUtil.get("plazofijo.simulacion.text"),
                            hintText: configUtil.get("plazofijo.simulacion.text"),
                            hintClassName: "max-width-30rem",
                        })}
                        alwaysShowMessage={isDesktop}
                    />
                </Box>
            )}
            <Box pt={!isDesktop ? 24 : 0} px={!isDesktop ? 16 : 40}>
                <CreateFixedTermDeposit
                    key={formInstance}
                    initialValuesSimulate={initialValuesSimulate}
                    initialValuesCreate={initialValuesCreate}
                    onSubmitSimulate={handleSubmitSimulate}
                    onSubmitCreate={handleSubmitCreate}
                    setFormikProps={setFormikPropsRef}
                    validationSchemaSimulate={validationSchemaSimulate}
                    validationSchemaCreate={validationSchemaCreate}
                    getFormLabel={getFormLabel}
                    loading={loading}
                    loadingSubmit={loadingSubmit}
                    accountsOptions={accountsOptions}
                    typeOptions={typeOptions}
                    selectedCurrency={selectedCurrency}
                    minAmount={minAmount}
                    onTypeChange={handleTypeChange}
                    minDueDate={minDueDate}
                    maxDueDate={maxDueDate}
                    handleCancel={handleCancel}
                    simulateData={simulateData}
                    automaticRenewalTooltipText={i18n.get(
                        "deposits.constitute.confirmation.automatic.renewal.information",
                    )}
                    automaticRenewalIsChecked={automaticRenewal}
                    onAutomaticRenewalChange={handleAutomaticRenewalChange}
                    onDueDateChange={handleDueDateChange}
                    onTermChange={handleTermChange}
                    onTermBlur={handleTermBlur}
                    actionToDueOptions={actionToDueOptions}
                    handleSimulateAgain={handleSimulateAgain}
                    showCreateForm={showCreateForm}
                    bottomText={i18n.get("deposits.constitute.confirmation.information")}
                    infoText={!isDesktop ? configUtil.get("plazofijo.simulacion.text") : undefined}
                />
            </Box>
        </>
    );
}

DepositsConstituteV2.propTypes = {
    dispatch: func.isRequired,
    loggedUser: shape({
        userId: string.isRequired,
    }).isRequired,
    accounts: arrayOf(
        shape({
            idProduct: string.isRequired,
        }),
    ).isRequired,
    fetchingAccounts: bool.isRequired,
    minAmountPesos: number,
    minAmountDolares: number,
    depositTypes: arrayOf(shape({})).isRequired,
    environmentDate: string,
    defaultDaysQuantity: number.isRequired,
    loadingPreview: bool.isRequired,
    loadingSimulation: bool.isRequired,
    loadingConstitution: bool.isRequired,
    simulationResult: shape({}),
    isRetailEnvironment: bool.isRequired,
    isDesktop: bool,
    location: shape({
        pathname: string,
    }),
    lastPathname: string.isRequired,
    embeddedInV3Shell: bool,
};

DepositsConstituteV2.defaultProps = {
    minAmountPesos: null,
    minAmountDolares: null,
    environmentDate: null,
    simulationResult: null,
    isDesktop: false,
    location: null,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    loggedUser: sessionSelectors.getUser(state),
    accounts: accountsSelectors.getAccounts(state),
    fetchingAccounts: accountsSelectors.getFetching(state),
    minAmountPesos: depositsSelectors.getMinAmountPesos(state),
    minAmountDolares: depositsSelectors.getMinAmountDolares(state),
    simulationResult: depositsSelectors.getSimulationResult(state),
    depositTypes: depositsSelectors.getDepositTypes(state),
    environmentDate: depositsSelectors.getEnvironmentDate(state),
    defaultDaysQuantity: depositsSelectors.getDefaultDaysQuantity(state),
    loadingPreview: depositsSelectors.getLoadingPreview(state),
    loadingSimulation: depositsSelectors.getLoadingSimulation(state),
    loadingConstitution: depositsSelectors.getLoadingConstitution(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
    lastPathname: statusSelectors.getPathname(state),
});

export default compose(withRouter, connect(mapStateToProps), resizableRoute)(DepositsConstituteV2);
